import React, { useMemo, useState } from "react";

const PAGE_SIZE = 5; // number of dates per page

const AttendanceTrackingReport = ({ empTask, disablePagination = false }) => {
  const groupedData = useMemo(() => {
    const grouped = {};

    empTask.forEach((log) => {
      const empKey = log.employeeID;
      if (!grouped[empKey]) {
        grouped[empKey] = {
          employeeID: log.employeeID,
          employeeCode: log.employeeCode,
          employeeName: log.employeeName,
          department: log.departmentName || "-",
          dailyLogs: {},
        };
      }

      const dateKey = log.attendanceDate;
      if (!grouped[empKey].dailyLogs[dateKey]) {
        grouped[empKey].dailyLogs[dateKey] = [];
      }

      const exists = grouped[empKey].dailyLogs[dateKey].some(
        (l) => l.punchIn === log.punchInTime && l.punchOut === log.punchOutTime
      );
      if (!exists) {
        grouped[empKey].dailyLogs[dateKey].push({
          punchIn: log.punchInTime,
          punchOut: log.punchOutTime,
          duration: log.duration,
        });
      }
    });

    return Object.values(grouped);
  }, [empTask]);

  return (
    <div className="p-4">
      {groupedData.map((emp, index) => (
        <EmployeeTable key={index} employee={emp} disablePagination={disablePagination} />
      ))}
    </div>
  );
};

const EmployeeTable = ({ employee, disablePagination = false }) => {
  const dates = Object.keys(employee.dailyLogs).sort();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(dates.length / PAGE_SIZE);
  const datesToDisplay = disablePagination ? dates : dates.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="border rounded-lg p-2 mb-4 bg-white shadow-sm">
      <div className="mb-2 text-xs">
        <strong>Employee ID:</strong> {employee.employeeCode  } &nbsp;|&nbsp;
        <strong>Name:</strong> {employee.employeeName} &nbsp;|&nbsp;
        <strong>Department:</strong> {employee.department}
      </div>

      <div style={{ maxHeight: disablePagination ? "auto !important" : "250px", overflowY: disablePagination ? "visible !important" : "auto" }} className="overflow-x-auto">
        <table className="w-full border table-fixed text-xs">
          <thead className="bg-blue-100 sticky top-0 z-10">
            <tr>
              <th className="border p-1">Date</th>
              <th className="border p-1">First In</th>
              <th className="border p-1">Last Out</th>
              <th className="border p-1">Total Hours</th>
              <th className="border p-1">Inside Office Hours</th>
              <th className="border p-1">Punch In</th>
              <th className="border p-1">Punch Out</th>
            </tr>
          </thead>
          <tbody>
            {datesToDisplay.map((date, i) => {
              const logs = processLogs(employee.dailyLogs[date]);
              const summary = calculateDaySummary(logs);

              return logs.map((log, j) => (
                <tr key={`${i}-${j}`}>
                  {j === 0 && (
                    <>
                      <td className="border p-1" rowSpan={logs.length}>{formatDateOnly(date)}</td>
                      <td className="border p-1" rowSpan={logs.length}>{formatTime(summary.firstIn)}</td>
                      <td className="border p-1" rowSpan={logs.length}>{formatTime(summary.lastOut)}</td>
                      <td className="border p-1" rowSpan={logs.length}>{summary.total}</td>
                      <td className="border p-1" rowSpan={logs.length}>{logs.length && logs[0].duration ? logs[0].duration : "-"}</td>
                    </>
                  )}
                  <td className="border p-1">{log.punchIn ? formatTime(log.punchIn) : "Missed Punch"}</td>
                  <td className="border p-1">{log.punchOut ? formatTime(log.punchOut) : "Missed Punch"}</td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>

      {!disablePagination && (
        <div className="flex justify-center mt-2 gap-2 text-xs">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// --- Utilities ---
const processLogs = (logs) => {
  const punches = [];
  logs.forEach((l) => {
    punches.push({ time: isValidTime(l.punchIn) ? l.punchIn : null, type: "in", duration: l.duration });
    punches.push({ time: isValidTime(l.punchOut) ? l.punchOut : null, type: "out", duration: l.duration });
  });

  const validPunches = punches.filter((p) => p.time);
  validPunches.sort((a, b) => new Date(a.time) - new Date(b.time));

  const result = [];
  let pendingIn = null;

  validPunches.forEach((p) => {
    if (p.type === "in") {
      if (pendingIn) result.push({ punchIn: pendingIn.time, punchOut: null, duration: pendingIn.duration });
      pendingIn = p;
    } else {
      if (pendingIn) {
        result.push({ punchIn: pendingIn.time, punchOut: p.time, duration: pendingIn.duration });
        pendingIn = null;
      } else {
        result.push({ punchIn: null, punchOut: p.time, duration: p.duration });
      }
    }
  });

  if (pendingIn) result.push({ punchIn: pendingIn.time, punchOut: null, duration: pendingIn.duration });
  return result;
};

const isValidTime = (timeStr) => timeStr && !timeStr.startsWith("0001-01-01") && !isNaN(new Date(timeStr));
const formatDateOnly = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : "-");
const formatTime = (timeStr) => (timeStr ? new Date(timeStr).toLocaleTimeString("en-GB", { hour12: false }) : "Missed Punch");

const calculateDuration = (start, end) => {
  if (!start || !end) return "-";
  let diff = Math.floor((end - start) / 1000);
  const hrs = String(Math.floor(diff / 3600)).padStart(2, "0");
  diff %= 3600;
  const mins = String(Math.floor(diff / 60)).padStart(2, "0");
  const secs = String(diff % 60).padStart(2, "0");
  return `${hrs}:${mins}:${secs}`;
};

const calculateDaySummary = (logs) => {
  const inTimes = logs.filter((l) => isValidTime(l.punchIn)).map((l) => new Date(l.punchIn));
  const outTimes = logs.filter((l) => isValidTime(l.punchOut)).map((l) => new Date(l.punchOut));
  const firstIn = inTimes.length ? new Date(Math.min(...inTimes)) : null;
  const lastOut = outTimes.length ? new Date(Math.max(...outTimes)) : null;
  return { firstIn, lastOut, total: calculateDuration(firstIn, lastOut) };
};

export default AttendanceTrackingReport;